import {
  Conversation,
  useQueryGetAllConversations,
} from "@apis:conversation/conversation";
import { useQueryGetAllMessages } from "@apis:message/message";
import { BrandedLogo } from "@components:BrandedLogo";
import { useBotSelection } from "@hooks:useBotSelection";
import { useCheckAutoTitle } from "@hooks:useCheckAutoTitle";
import { useNewChat } from "@hooks:useNewChat";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Tab } from "@pages:app/talk/const";
import { HistoryItem } from "@pages:app/talk/Sidebar/HistoryItem";
import { Toggle } from "@pages:app/talk/Sidebar/Toggle";
import { keepPreviousData } from "@tanstack/react-query";
import { DateGroup, groupObjectByDate } from "@utils:datetime";
import _, { orderBy, uniqBy } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClockCircle } from "react-icons/ai";
import { BiMessageRoundedAdd } from "react-icons/bi";
import { BsLayoutSidebar } from "react-icons/bs";

import { FeatureFlag } from "@:flag";

// An LLM title can arrive late after the first answer; poll until it arrives
const TITLE_POLL_INTERVAL = 5 * 1000;
const TITLE_POLL_WINDOW = 3 * 60 * 1000;

const isAwaitingLlmTitle = (
  conversation: Conversation["GET_ALL"]["Response"][number],
) =>
  !conversation.llmTitle &&
  !conversation.userTitle &&
  Date.now() - new Date(conversation.createdAt).getTime() < TITLE_POLL_WINDOW;

interface SidebarProps {
  tab: Tab;
  setTab: (tab: Tab) => void;
  onToggleSidebar?: () => void;
}

export const Sidebar = ({ tab, setTab, onToggleSidebar }: SidebarProps) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { newChat } = useNewChat();

  const { selectedBotId } = useBotSelection();

  // Add keyboard shortcut listener for ⌘+K (Mac) and Ctrl+K (Windows)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for ⌘+K on Mac or Ctrl+K on Windows/Linux
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        newChat();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [newChat]);

  // TODO: We currently support Chat
  const messages = useQueryGetAllMessages({
    select: (data) => {
      return _(data)
        .filter((message) => message.botId === selectedBotId)
        .groupBy("conversationId")
        .map((messages) => _.orderBy(messages, ["createdAt"], ["asc"])[0])
        .value();
    },
    placeholderData: keepPreviousData,
  });

  const groupedMessages = useMemo(() => {
    return groupObjectByDate(messages.data || [], "createdAt");
  }, [messages.data]);

  const { isAutoTitleEnabled } = useCheckAutoTitle();

  const conversations = useQueryGetAllConversations({
    enabled: tab === Tab.Chat,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      isAutoTitleEnabled && query.state.data?.some(isAwaitingLlmTitle)
        ? TITLE_POLL_INTERVAL
        : false,
  });

  const conversationTitles = useMemo(() => {
    const titles = new Map<string, string>();
    conversations.data?.forEach((conversation) =>
      titles.set(conversation.id, conversation.title),
    );
    return titles;
  }, [conversations.data]);

  const [hoveredConversationId, setHoveredConversationId] = useState<
    string | undefined
  >();

  // Stable React key for a date bucket (locale-independent).
  const groupKey = (group: DateGroup) =>
    group.kind === "month" ? `${group.year}-${group.month}` : group.kind;

  // Localized header label: relative buckets via i18n, months via Luxon.
  const groupLabel = (group: DateGroup) =>
    group.kind === "month"
      ? DateTime.fromObject(
          { year: group.year, month: group.month },
          { locale: i18n.language },
        ).toFormat("LLLL yyyy")
      : t(`talk.dates.${group.kind}`);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        width: "275px",
        transform: "translateX(0)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          paddingTop: "2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <BrandedLogo />
        {onToggleSidebar && (
          <Tooltip title={t("talk.hideSidebar")} placement="right">
            <IconButton onClick={onToggleSidebar}>
              <BsLayoutSidebar size={20} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          mx: "auto",
        }}
      >
        {FeatureFlag.ToggleChatSearch && <Toggle tab={tab} setTab={setTab} />}
      </Box>

      <Box
        sx={{
          marginTop: "15px",
          mx: "auto",
          display: tab === Tab.Chat ? "block" : "none",
        }}
      >
        <Button
          variant="contained"
          startIcon={
            <Stack direction="row" alignItems="center" gap="4px">
              <BiMessageRoundedAdd size={20} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  lineHeight: "20px",
                }}
              >
                {t("talk.newChat")}
              </Typography>
            </Stack>
          }
          endIcon={
            <Stack direction="row" gap="3px">
              <Typography
                sx={{
                  padding: "4px 4px",
                  backgroundColor: "#E0E0E0",
                  borderRadius: "4px",
                  lineHeight: "16px",
                  fontWeight: 600,
                  width: "22px",
                }}
              >
                ⌘
              </Typography>
              <Typography
                sx={{
                  padding: "4px 4px",
                  backgroundColor: "#E0E0E0",
                  borderRadius: "4px",
                  lineHeight: "16px",
                  fontWeight: 600,
                  width: "22px",
                }}
              >
                K
              </Typography>
            </Stack>
          }
          sx={{
            height: "42px",
            backgroundColor: "#FFFFFF",
            color: "#1D1D1D",
            "&:hover": {
              backgroundColor: "#f9f9f9",
              boxShadow: "0px 8px 24px 0px #686B6E29",
            },
            padding: "21px 25px",
            borderRadius: "40px",
            gap: "1.5rem",
          }}
          onClick={() => {
            newChat();
          }}
        />
      </Box>

      {messages.isLoading ? (
        <Box
          sx={{
            display: "flex",
            height: "100%",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            sx={{
              color: theme.palette.primary.main,
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "1rem",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "0.5rem",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "0.25rem",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {groupedMessages.map(({ group, items }) => {
            let orderedItems = orderBy(items, ["createdAt"], ["desc"]);

            if (tab === Tab.Search) {
              orderedItems = uniqBy(orderedItems, "question");
            }

            return (
              <Box
                key={groupKey(group)}
                sx={{
                  marginTop: "1rem",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    marginTop: "20px",
                    marginBottom: "5px",
                    paddingLeft: "14px",
                  }}
                >
                  <AiOutlineClockCircle color="#a4a4aa" />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      lineHeight: "18px",
                      fontWeight: 500,
                      color: "#a4a4aa",
                    }}
                  >
                    {groupLabel(group)}
                  </Typography>
                </Stack>
                {orderedItems.map((message, i) => (
                  <HistoryItem
                    key={`${group}-${message.id}-${i}`}
                    message={message}
                    title={
                      tab === Tab.Chat
                        ? (conversationTitles.get(
                            message.conversationId as string,
                          ) ?? message.question)
                        : message.question
                    }
                    hoveredConversationId={hoveredConversationId}
                    setHoveredConversationId={setHoveredConversationId}
                    tab={tab}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
